import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { getSidebarCount } from '../services/dashboardService';
import { useRefresh } from '../utils/RefreshContext';

const useAnimatedCount = (target, duration = 1200) => {
    const [count, setCount] = useState(0);
    const prevTarget = useRef(0);

    useEffect(() => {
        if (target === 0) { setCount(0); return; }
        if (target === prevTarget.current) return;
        prevTarget.current = target;

        let startTime = null;
        const from = count;

        const step = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / duration, 1);
            const ease = progress * (2 - progress); // ease-out quad
            setCount(Math.round(from + (target - from) * ease));
            if (progress < 1) window.requestAnimationFrame(step);
        };

        window.requestAnimationFrame(step);
    }, [target]);

    return count;
};

const AnimatedBadge = ({ value }) => {
    const count = useAnimatedCount(value);
    return <>{count}</>;
};

const NAV = [
  { section: 'Main' },
  { path: '/', label: 'Dashboard', icon: <><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></> },
  { section: 'Management' },
  { path: '/users', label: 'User Management', icon: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></> },
  { path: '/categories', label: 'Categories', icon: <><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></> },
  { path: '/dynamic-fields', label: 'Dynamic Fields', icon: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></> },
  { path: '/airports', label: 'Airport Management', icon: <><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></> },
  { path: '/pois', label: 'POI Management', icon: <><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></> },
  { section: 'Flight Flows' },
  { path: '/airlines', label: 'Airlines', icon: <><circle cx="9" cy="7" r="4"/><path d="M3 21v-2a4 4 0 0 1 4-4h4"/><path d="M16 3.13a4 4 0 0 1 0 7.75M21 21v-2a4 4 0 0 0-3-3.87"/></> },
  { path: '/routes', label: 'Routes', icon: <><path d="M3 12h18M3 6h18M3 18h18"/></> },
  { path: '/flights', label: 'Flight Operations', icon: <><path d="M17.8 19.2 16 11l3.5-3.5C20.2 6.8 21.5 4 21 3.5c-.5-.5-3.3.8-4 1.5L13.5 8.5 5.3 6.7c-.5-.1-1.1.1-1.4.5l-1.3 1.3c-.4.4-.3 1.1.1 1.4l5.3 4.1-3 3-1.8-.6c-.4-.1-.8.1-1 .4l-.7.7c-.3.3-.3.9 0 1.2l2.3 2.3c.3.3.9.3 1.2 0l.7-.7c.3-.2.5-.6.4-1l-.6-1.8 3-3 4.1 5.3c.3.4 1 .5 1.4.1l1.3-1.3c.4-.3.6-.9.5-1.4z"/></> },
  { path: '/flight-alerts', label: 'Flight Alerts', icon: <><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></> },
  { path: '/sync-logs', label: 'Airport Sync Logs', icon: <><path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67"/></> },

  { path: '/security-lanes', label: 'Security Lane', icon: <><path d="M4 6h16M4 12h16M4 18h7"/></> },
  { path: '/security-checkpoints', label: 'Security Checkpoint', icon: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></> },
  { path: '/gates', label: 'Gate Management', icon: <><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 17V7h4a3 3 0 0 1 0 6H9"/></> },
   { path: '/pickup-zones', label: 'Pickup Zones', icon: <><rect x="1" y="3" width="15" height="13" rx="2"/><path d="M16 8h4l3 3v5h-7V8z"/></> },
   { section: 'System' },
   { path: '/settings', label: 'Settings', icon: <><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 19.07a10 10 0 0 1 0-14.14"/></> },
];

const Sidebar = ({ isOpen, onClose, onLogout }) => {
    const location = useLocation();
    const { triggerRefresh } = useRefresh();
    const [userCount, setUserCount] = useState(0);
    const [adminUser, setAdminUser] = useState(null);

    useEffect(() => {
        // Only fetch count once on initial mount, don't fetch on every page change
        const fetchCount = async () => {
            try {
                const response = await getSidebarCount();
                const count = response?.data?.users?.total || response?.users?.total || response?.data?.users || 0;
                setUserCount(count);
            } catch (error) {
                console.error('Sidebar count fetch failed:', error);
                setUserCount(0);
            }
        };

        fetchCount();

        try {
            const userStr = localStorage.getItem('adminUser');
            if (userStr) setAdminUser(JSON.parse(userStr));
        } catch(e) { console.error('Failed to parse adminUser', e); }
    }, []); // Empty dependency array - runs only once on component mount

    const getInitials = (name) => {
        if (!name) return 'A';
        return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
    };

    return (
        <aside className={`sidebar${isOpen ? ' sidebar-open' : ''}`}>

            {/* CLOSE BUTTON (Mobile) */}
            <button onClick={onClose} className="sidebar-close-btn">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
            </button>

            {/* LOGO */}
            <div style={{ padding: '24px 24px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 22, fontWeight: 700, color: 'white' }}>
                    Gate<span style={{ color: 'var(--gold)' }}>Guide</span>
                </div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 2, letterSpacing: 1, textTransform: 'uppercase' }}>
                    Admin Panel
                </div>
            </div>

            {/* NAVIGATION */}
            <nav style={{ flex: 1, padding: '12px 0', overflowY: 'auto' }}>
                {NAV.map((item, i) => {
                    if (item.section) return (
                        <div key={i} style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.25)', letterSpacing: '1.2px', textTransform: 'uppercase', padding: '14px 24px 6px' }}>
                            {item.section}
                        </div>
                    );

                    // Custom Active Logic to handle path + hash correctly
                    const isHashLink = item.path.includes('#');
                    const isActive = isHashLink 
                        ? (location.pathname + location.hash) === item.path
                        : (location.pathname === item.path && location.hash === '');

                    return (
                        <NavLink 
                            key={i} 
                            to={item.path} 
                            onClick={(e) => {
                                if (isActive) {
                                    triggerRefresh();
                                }
                                onClose();
                            }}
                            style={{
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 12,
                                padding: '10px 24px', 
                                cursor: 'pointer', 
                                textDecoration: 'none',
                                borderLeft: `3px solid ${isActive ? 'var(--blue)' : 'transparent'}`,
                                background: isActive ? 'rgba(102,178,255,0.12)' : 'transparent',
                                margin: '1px 0', 
                                transition: 'all 0.15s',
                            }}
                        >
                            <svg 
                                width="17" 
                                height="17" 
                                viewBox="0 0 24 24" 
                                fill="none"
                                stroke={isActive ? 'var(--blue)' : 'rgba(255,255,255,0.4)'} 
                                strokeWidth="1.8" 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                style={{ flexShrink: 0 }}
                            >
                                {item.icon}
                            </svg>
                            <span style={{ 
                                fontSize: 13, 
                                fontWeight: isActive ? 600 : 500, 
                                color: isActive ? 'var(--blue)' : 'rgba(255,255,255,0.5)', 
                                flex: 1 
                            }}>
                                {item.label}
                            </span>

                            {/* Dynamic User Count Badge - Only for User Management */}
                            {item.label === 'User Management' && (
                                <>
                                    <style>{`
                                        @keyframes badgePulse {
                                            0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(239,68,68,0.5); transform: scale(1); }
                                            50% { opacity: 0.85; box-shadow: 0 0 0 4px rgba(239,68,68,0); transform: scale(1.08); }
                                        }
                                        .user-count-badge {
                                            animation: badgePulse 2s infinite ease-in-out;
                                        }
                                    `}</style>
                                    <span
                                        className="user-count-badge"
                                        style={{ 
                                            background: '#ef4444', 
                                            color: 'white', 
                                            fontSize: 10, 
                                            fontWeight: 700, 
                                            padding: '2px 8px', 
                                            borderRadius: 9999,
                                            minWidth: 20,
                                            textAlign: 'center',
                                            display: 'inline-block',
                                            transition: 'transform 0.2s'
                                        }}
                                    >
                                        <AnimatedBadge value={userCount} />
                                    </span>
                                </>
                            )}
                        </NavLink>
                    );
                })}
            </nav>

            {/* FOOTER */}
            <div style={{ padding: '14px 24px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'linear-gradient(135deg,#66B2FF,#0A1F44)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: 'white', flexShrink: 0 }}>
                        {adminUser ? getInitials(adminUser.full_name) : 'SA'}
                    </div>
                    <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'white' }}>{adminUser ? adminUser.full_name : 'Super Admin'}</div>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>{adminUser ? adminUser.email : 'admin@gateguide.io'}</div>
                    </div>
                </div>
                <button onClick={onLogout} style={{
                    width: '100%', padding: '9px 14px', background: 'rgba(255,92,92,0.1)',
                    border: '1px solid rgba(255,92,92,0.2)', borderRadius: 10,
                    color: 'var(--red)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"/>
                    </svg>
                    Log Out
                </button>
            </div>
        </aside>
    );
};

export default Sidebar;